import { FabricEditor } from 'modules/fabric-editor';
import { loadCanvasJson, RENDER_PLUGINS } from 'modules/manager/domain/headless-canvas';
import { findLayoutGroup } from 'modules/manager/domain/layout-blocks';
import type { ResolverContext } from 'modules/manager/domain/placeholder';
import type { TemplateType } from 'modules/manager/domain/template-type';
import { ensureFonts } from 'shared/fonts';
import { substitutePlaceholders } from './substitute-placeholders';

/** What of a media-manager template the frame needs — `MediaManagerTemplate` fits as is. */
export interface TemplateFrameSource {
  objects: unknown[];
  width: number;
  height: number;
  templateType?: TemplateType;
}

/**
 * A template picture with a cross-origin image drawn without CORS: the canvas is tainted, and the encoder refuses
 * a `VideoFrame` from it with a `SecurityError` in the middle of the assembly. Caught here, before it starts.
 */
export class TemplateFrameError extends Error {
  constructor(readonly code: 'tainted') {
    super(code);
    this.name = 'TemplateFrameError';
  }
}

interface FabricJsonNode {
  type?: unknown;
  crossOrigin?: unknown;
  objects?: unknown;
}

/** Images of the template load with CORS, so drawing them keeps the canvas readable. */
function withCrossOrigin(objects: readonly unknown[]): unknown[] {
  return objects.map((item) => {
    if (!item || typeof item !== 'object') return item;
    const node = { ...(item as FabricJsonNode) };
    if (node.type === 'image' && !node.crossOrigin) {
      node.crossOrigin = 'anonymous';
    }
    if (Array.isArray(node.objects)) {
      node.objects = withCrossOrigin(node.objects);
    }
    return node;
  });
}

/**
 * The layout block is the editor's cue for background photo slots («ФОН», «IMAGE»). A review has no background
 * photos, so the slots stay empty — as the media manager's own render did with no photo URLs (`77da087f`).
 */
function removeLayoutBlock(editor: FabricEditor) {
  const group = findLayoutGroup(editor.canvas);
  if (group) {
    editor.canvas.remove(group);
  }
}

function assertReadable(canvas: HTMLCanvasElement) {
  try {
    canvas.getContext('2d')?.getImageData(0, 0, 1, 1);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'SecurityError') {
      throw new TemplateFrameError('tainted');
    }
    throw error;
  }
}

/**
 * Draws a media-manager template with the match data put into its placeholders and returns the picture at the
 * template's own size. The template stores no background, so whatever the author left empty stays transparent —
 * a plate is drawn over the clip as is. The caller owns the bitmap and closes it.
 *
 * The canvas is a detached element on a plain `FabricEditor` with the preview's plugin set: no React, nothing on the
 * page. Fonts are awaited before the placeholders are fitted, or the text is measured with the fallback font.
 */
export async function renderTemplateFrame(template: TemplateFrameSource, ctx: ResolverContext): Promise<ImageBitmap> {
  const canvasEl = Object.assign(document.createElement('canvas'), { width: template.width, height: template.height });
  const editor = new FabricEditor({
    canvasEl,
    resolution: { width: template.width, height: template.height },
    // One canvas pixel = one picture pixel: on a HiDPI screen retina scaling would double the bitmap.
    fabricOptions: { enableRetinaScaling: false, selection: false, renderOnAddRemove: false },
  });
  try {
    for (const plugin of RENDER_PLUGINS) editor.use(plugin);
    // Before the load, as in the preview: the placeholder plugin checks keys against the type.
    editor.placeholders?.setTemplateType(template.templateType);
    const objects = withCrossOrigin(template.objects);
    await loadCanvasJson(editor, objects);
    await ensureFonts(objects);
    editor.fonts?.refresh();
    removeLayoutBlock(editor);
    await substitutePlaceholders(editor, ctx, template.templateType);
    editor.canvas.renderAll();

    const element = editor.canvas.getElement();
    assertReadable(element);
    return await createImageBitmap(element);
  } finally {
    editor.dispose();
  }
}
